import { Button, cn, Input, Modal, Select, Spinner } from '@sjuni/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileText, UploadCloud } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/components/FormError';
import { SubmitButton } from '@/components/SubmitButton';
import { apiFetch } from '@/lib/api/client';
import { adminKeys, useResourceList } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { valueFor } from '@/lib/forms/translatable';

import {
  DOCUMENT_LEVELS,
  DOCUMENT_SCOPES,
  type DocumentCategoryRow,
  type DocumentInput,
  type DocumentLevel,
  type DocumentRow,
  type DocumentScope,
  formatFileSize,
  type UploadResult,
} from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.4 — "Upload: drag-and-drop, multiple files,
 * with a bulk metadata step (apply category / scope / level / year to all, then
 * adjust individually). This matters — migration will land hundreds of PDFs
 * that need categorising."
 *
 * Two steps, in this order because the order is the feature. Dropping files
 * starts the transfer straight away and asks nothing; only once they are up
 * does the form appear, with one set of controls that writes to every row and a
 * title field per row. Asking for a category per file at drop time would mean
 * three hundred questions before the first byte moves.
 *
 * The rows already exist as drafts by the time the second step opens — the
 * server creates them on upload. So an abandoned dialog leaves drafts in the
 * list rather than orphaned files on a disk nobody can see.
 */
export interface DocumentUploadDialogProps {
  onClose: () => void;
}

type Step = 'drop' | 'metadata';

export function DocumentUploadDialog({ onClose }: DocumentUploadDialogProps) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const { user, hasRole } = useAuth();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('drop');
  const [dragging, setDragging] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [titles, setTitles] = useState<Record<number, string>>({});

  const inputRef = useRef<HTMLInputElement>(null);

  const facultyScoped = hasRole('faculty-admin');
  const vocationalScoped = hasRole('vocational-admin');

  // §5.4's "apply … to all", held once for the whole batch.
  const [shared, setShared] = useState<{
    category_id: string;
    scope: DocumentScope;
    level: DocumentLevel;
    year: string;
  }>({
    category_id: '',
    // A scoped role has one legal answer, and the server enforces it — so the
    // control opens on it rather than on something that would be refused.
    scope: vocationalScoped ? 'vocational' : facultyScoped ? 'faculty' : 'university',
    level: vocationalScoped ? 'vocational' : 'all',
    year: String(new Date().getFullYear()),
  });

  const categories = useResourceList<DocumentCategoryRow>('document-categories', {
    per_page: 100,
    sort: 'sort_order',
  });

  const upload = useMutation({
    mutationFn: (files: File[]) => {
      const form = new FormData();

      for (const file of files) {
        form.append('files[]', file);
      }

      return apiFetch<UploadResult>('/admin/documents/upload', { method: 'POST', body: form });
    },
    onSuccess: (data) => {
      setResult(data);
      setTitles(Object.fromEntries(data.created.map((row) => [row.id, valueFor(row.title, 'ka')])));
      setStep('metadata');
      void queryClient.invalidateQueries({ queryKey: adminKeys.resource('documents') });
    },
    onError: (error: Error) => setFormError(error.message),
  });

  /**
   * §5.4's metadata step, applied.
   *
   * One PUT per row rather than a bulk endpoint. §6 defines none, and a bulk
   * write would have to skip the per-record policy check that is the whole of
   * §3's faculty and vocational scoping.
   */
  const save = useMutation({
    mutationFn: async (rows: DocumentRow[]) => {
      for (const row of rows) {
        const input: DocumentInput = {
          title: { ka: titles[row.id] ?? valueFor(row.title, 'ka') },
          description: {},
          category_id: Number(shared.category_id),
          scope: shared.scope,
          faculty_id: shared.scope === 'faculty' ? (user?.faculty?.id ?? row.faculty_id) : null,
          level: shared.level,
          year: shared.year === '' ? null : Number(shared.year),
          document_date: null,
          is_public: true,
          sort_order: row.sort_order,
          // Drafts. §5.4 is a filing step, not a publishing one — the editor
          // publishes from the list once the metadata is right.
          status: 'draft',
          published_at: null,
          pending_review: row.pending_review,
        };

        await apiFetch<{ data: DocumentRow }>(`/admin/documents/${row.id}`, {
          method: 'PUT',
          body: input,
        });
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.resource('documents') });
      onClose();
    },
    onError: (error: Error) => setFormError(error.message),
  });

  const accept = (files: FileList | null) => {
    setFormError(null);

    const list = Array.from(files ?? []);

    if (list.length > 0) {
      upload.mutate(list);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('documents.upload.title')}
      closeLabel={t('common.close')}
      className="sm:w-[min(94vw,860px)]"
    >
      <FormError message={formError} />

      {step === 'drop' ? (
        <div className="space-y-4">
          <p className="text-body-sm text-secondary">{t('documents.upload.intro')}</p>

          {/*
            A label wrapping a file input, not a div with a click handler. The
            input is the control — it opens the picker from the keyboard, it is
            announced as a file field, and the drop target is decoration on top
            of something that already works without a pointer.
          */}
          <label
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              accept(event.dataTransfer.files);
            }}
            className={cn(
              'flex cursor-pointer flex-col items-center gap-3 rounded-card border-2 border-dashed p-10 text-center transition-colors duration-fast',
              'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-navy-600',
              dragging ? 'border-navy-600 bg-accent-tint-navy' : 'border-strong',
            )}
          >
            <UploadCloud size={32} strokeWidth={1.5} aria-hidden="true" className="text-muted" />

            <span className="text-body font-semibold text-primary">
              {t('documents.upload.drop')}
            </span>
            <span className="text-caption text-muted">{t('documents.upload.formats')}</span>

            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
              className="sr-only"
              onChange={(event) => accept(event.target.files)}
            />
          </label>

          {upload.isPending ? (
            <p className="flex items-center justify-center gap-2 text-body-sm text-secondary">
              <Spinner size="sm" label={t('documents.upload.working')} />
              {t('documents.upload.working')}
            </p>
          ) : null}
        </div>
      ) : (
        <form
          noValidate
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            setFormError(null);
            save.mutate(result?.created ?? []);
          }}
        >
          {/* §5.4's "this file already exists as X". */}
          {result && result.duplicates.length > 0 ? (
            <div
              role="status"
              className="space-y-1 rounded-card border-s-4 border-gold-500 bg-surface-alt p-3 text-caption text-secondary"
            >
              <p className="flex items-center gap-2 font-semibold text-primary">
                <AlertTriangle size={14} strokeWidth={1.75} aria-hidden="true" />
                {t('documents.upload.duplicatesTitle')}
              </p>

              <ul className="space-y-0.5">
                {result.duplicates.map((duplicate) => (
                  <li key={duplicate.file_name}>
                    {t('documents.upload.duplicate', {
                      file: duplicate.file_name,
                      title: duplicate.existing.title,
                    })}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {result && result.created.length === 0 ? (
            <p className="text-body-sm text-secondary">{t('documents.upload.nothingNew')}</p>
          ) : (
            <>
              <fieldset className="space-y-4 rounded-card bg-surface-alt p-4">
                <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
                  {t('documents.upload.sharedTitle')}
                </legend>

                <p className="text-caption text-muted">{t('documents.upload.sharedHint')}</p>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    label={t('documents.fields.category')}
                    required
                    value={shared.category_id}
                    onChange={(event) =>
                      setShared((current) => ({ ...current, category_id: event.target.value }))
                    }
                    options={[
                      { value: '', label: t('documents.fields.categoryEmpty') },
                      ...(categories.data?.data ?? []).map((category) => ({
                        value: String(category.id),
                        label: valueFor(category.name, locale),
                      })),
                    ]}
                  />

                  <Select
                    label={t('documents.fields.scope')}
                    value={shared.scope}
                    // A scoped role has one legal answer; the server refuses
                    // anything else, so the control does not offer it.
                    disabled={facultyScoped || vocationalScoped}
                    onChange={(event) =>
                      setShared((current) => ({
                        ...current,
                        scope: event.target.value as DocumentScope,
                      }))
                    }
                    options={DOCUMENT_SCOPES.map((scope) => ({
                      value: scope,
                      label: t(`documents.scopes.${scope}`),
                    }))}
                  />

                  <Select
                    label={t('documents.fields.level')}
                    value={shared.level}
                    onChange={(event) =>
                      setShared((current) => ({
                        ...current,
                        level: event.target.value as DocumentLevel,
                      }))
                    }
                    options={DOCUMENT_LEVELS.map((level) => ({
                      value: level,
                      label: t(`documents.levels.${level}`),
                    }))}
                  />

                  <Input
                    type="number"
                    min={1990}
                    label={t('documents.fields.year')}
                    value={shared.year}
                    onChange={(event) =>
                      setShared((current) => ({ ...current, year: event.target.value }))
                    }
                  />
                </div>
              </fieldset>

              {/* "…then adjust individually" — the title is the part that has
                  to differ per file, and it starts as the filename. */}
              <ul className="space-y-3">
                {(result?.created ?? []).map((row) => (
                  <li key={row.id} className="flex flex-wrap items-end gap-3">
                    <span className="flex min-w-40 flex-1 items-center gap-2 text-caption text-muted">
                      <FileText size={14} strokeWidth={1.75} aria-hidden="true" />
                      <span className="truncate">{row.file_name}</span>
                      <span className="whitespace-nowrap">
                        {formatFileSize(row.file_size, (key) => t(`documents.units.${key}`))}
                      </span>
                    </span>

                    <Input
                      label={t('documents.fields.title')}
                      hideLabel
                      className="min-w-56 flex-[2]"
                      value={titles[row.id] ?? ''}
                      onChange={(event) =>
                        setTitles((current) => ({ ...current, [row.id]: event.target.value }))
                      }
                    />
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={save.isPending}>
              {result && result.created.length === 0 ? t('common.close') : t('common.cancel')}
            </Button>

            {result && result.created.length > 0 ? (
              <SubmitButton loading={save.isPending} disabled={shared.category_id === ''}>
                {t('documents.upload.save', { count: result.created.length })}
              </SubmitButton>
            ) : null}
          </div>
        </form>
      )}
    </Modal>
  );
}
